import { useState } from 'react';

import type { TerminalOut, TerminalTypeOut } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, useAsync } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * 단말 모델 관리 — 레거시 define/terminal 파리티.
 * 타입(패널/인버터 등) 필터 + 검색, 등록/수정/삭제. 회사 격리는 서버가 강제한다.
 */

const SIZE = 30;

export function TerminalManage() {
  const [typeId, setTypeId] = useState<number | ''>('');
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const types = useAsync(() => api.listTerminalTypes(), []);
  const list = useAsync(
    () =>
      api.listTerminals({
        type_id: typeId === '' ? undefined : typeId,
        keyword: applied,
        page,
        size: SIZE,
      }),
    [typeId, applied, page],
  );

  const typeName = (id: number | null) =>
    types.data?.find((t) => t.id === id)?.name ?? (id ?? '-');

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));
  const row = list.data?.items.find((t) => t.id === selected) ?? null;

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          <button
            type="button"
            className={typeId === '' ? 'chip active' : 'chip'}
            onClick={() => {
              setTypeId('');
              setPage(1);
            }}
          >
            전체
          </button>
          {(types.data ?? []).map((t) => (
            <button
              key={t.id}
              type="button"
              className={typeId === t.id ? 'chip active' : 'chip'}
              onClick={() => {
                setTypeId(t.id);
                setPage(1);
              }}
            >
              {t.name}
            </button>
          ))}

          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(keyword.trim());
              setPage(1);
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="단말 명칭 · 모델"
            />
            <button type="submit">검색</button>
          </form>
          <button type="button" className="primary create" onClick={() => setCreateOpen(true)}>
            등록
          </button>
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>타입</th>
                <th>단말 명칭</th>
                <th>제조사</th>
                <th>모델</th>
                <th className="num">용량</th>
                <th>규격</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    등록된 단말이 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((t) => (
                <tr
                  key={t.id}
                  className={t.id === selected ? 'selected' : ''}
                  onClick={() => setSelected(t.id)}
                >
                  <td>{typeName(t.def_terminal_type_id)}</td>
                  <td>{t.name}</td>
                  <td>{t.maker ?? '-'}</td>
                  <td>{t.model ?? '-'}</td>
                  <td className="num">{t.capacity ?? '-'}</td>
                  <td>{t.spec ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>

      <aside className="admin__aside">
        {row === null ? (
          <p className="muted">단말을 선택하세요.</p>
        ) : (
          <TerminalEdit
            key={row.id}
            row={row}
            types={types.data ?? []}
            onClose={() => setSelected(null)}
            onChanged={() => list.reload()}
            onDeleted={() => {
              setSelected(null);
              list.reload();
            }}
          />
        )}
      </aside>

      {createOpen && (
        <TerminalCreateModal
          types={types.data ?? []}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            list.reload();
          }}
        />
      )}
    </div>
  );
}

function toInt(value: string): number | undefined {
  const n = Number(value);
  return value.trim() !== '' && Number.isFinite(n) ? Math.trunc(n) : undefined;
}

function TerminalEdit({
  row,
  types,
  onClose,
  onChanged,
  onDeleted,
}: {
  row: TerminalOut;
  types: TerminalTypeOut[];
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [typeId, setTypeId] = useState(
    row.def_terminal_type_id === null ? '' : String(row.def_terminal_type_id),
  );
  const [name, setName] = useState(row.name);
  const [model, setModel] = useState(row.model ?? '');
  const [capacity, setCapacity] = useState(row.capacity === null ? '' : String(row.capacity));
  // 제조사/규격 — 2026 보급사업 공고가 모듈·인버터 사양 기재를 요구해 추가(선택).
  const [maker, setMaker] = useState(row.maker ?? '');
  const [spec, setSpec] = useState(row.spec ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function save() {
    if (!name.trim() || !model.trim() || typeId === '') {
      setError('타입 · 단말 명칭 · 모델을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await api.updateTerminal(row.id, {
        def_terminal_type_id: Number(typeId),
        name: name.trim(),
        model: model.trim(),
        capacity: toInt(capacity),
        // 빈칸은 null 로 보내 기존 값을 지울 수 있게 한다(undefined 면 서버가 무시).
        maker: maker.trim() || null,
        spec: spec.trim() || null,
      });
      setOk('저장했습니다.');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteTerminal(row.id);
      onDeleted();
    } catch (err) {
      // 상품 구성에 쓰이는 단말 등 삭제 가드 409 메시지를 그대로 보여준다.
      setError(err instanceof ApiError ? err.message : '삭제하지 못했습니다.');
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>{row.name}</h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel form-grid">
        <label className="f wide">
          타입 *
          <select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">선택</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label className="f wide">
          단말 명칭 *
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="f wide">
          제조사
          <input value={maker} onChange={(e) => setMaker(e.target.value)} placeholder="예: 한화큐셀" />
        </label>
        <label className="f wide">
          모델 *
          <input value={model} onChange={(e) => setModel(e.target.value)} />
        </label>
        <label className="f wide">
          용량
          <input type="number" min={0} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
        </label>
        <label className="f wide">
          규격
          <input value={spec} onChange={(e) => setSpec(e.target.value)} placeholder="예: 445W 단결정" />
        </label>
      </section>

      {error && <p className="error">{error}</p>}
      {ok && <p className="ok">{ok}</p>}

      <div className="row form-actions">
        <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>
          삭제
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>

      {confirmDelete && (
        <Modal title="단말 삭제" onClose={() => setConfirmDelete(false)}>
          <p>
            <strong>{row.name}</strong> 단말을 삭제합니다. 계속할까요?
          </p>
          <div className="row">
            <button type="button" onClick={() => setConfirmDelete(false)}>
              취소
            </button>
            <button type="button" className="primary" disabled={busy} onClick={() => void remove()}>
              삭제
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TerminalCreateModal({
  types,
  onClose,
  onCreated,
}: {
  types: TerminalTypeOut[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [typeId, setTypeId] = useState('');
  const [name, setName] = useState('');
  const [model, setModel] = useState('');
  const [capacity, setCapacity] = useState('');
  const [maker, setMaker] = useState('');
  const [spec, setSpec] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (typeId === '' || !name.trim() || !model.trim()) {
      setError('타입 · 단말 명칭 · 모델을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createTerminal({
        def_terminal_type_id: Number(typeId),
        name: name.trim(),
        model: model.trim(),
        capacity: toInt(capacity),
        maker: maker.trim() || undefined,
        spec: spec.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="단말 등록" onClose={onClose}>
      <label className="labeled">
        타입 *
        <select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          <option value="">선택</option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <label className="labeled">
        단말 명칭 *
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="labeled">
        제조사
        <input value={maker} onChange={(e) => setMaker(e.target.value)} placeholder="예: 한화큐셀" />
      </label>
      <label className="labeled">
        모델 *
        <input value={model} onChange={(e) => setModel(e.target.value)} />
      </label>
      <label className="labeled">
        용량
        <input type="number" min={0} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
      </label>
      <label className="labeled">
        규격
        <input value={spec} onChange={(e) => setSpec(e.target.value)} placeholder="예: 445W 단결정" />
      </label>

      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '등록 중…' : '등록'}
        </button>
      </div>
    </Modal>
  );
}
